import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';

const HeroSection = () => {
  const navigate = useNavigate();

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <section className="relative min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-white to-green-50">
      {/* Navbar */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-md border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 flex items-center justify-center bg-gradient-to-br from-blue-500 to-green-500 rounded-lg">
              <i className="ri-heart-pulse-line text-white text-xl"></i>
            </div>
            <span className="text-xl font-bold text-gray-900">Liver Health Check</span>
          </div>
          <div className="flex items-center gap-6">
            <button
              onClick={() => scrollToSection('about')}
              className="text-sm font-medium text-gray-700 hover:text-blue-600 transition-colors cursor-pointer"
            >
              サービスについて
            </button>
            <button
              onClick={() => scrollToSection('how-it-works')}
              className="text-sm font-medium text-gray-700 hover:text-blue-600 transition-colors cursor-pointer"
            >
              使い方
            </button>
            <button
              onClick={() => scrollToSection('faq')}
              className="text-sm font-medium text-gray-700 hover:text-blue-600 transition-colors cursor-pointer"
            >
              よくある質問
            </button>
          </div>
        </div>
      </nav>

      {/* Hero Content */}
      <div className="max-w-7xl mx-auto px-6 pt-32 pb-20 text-center">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6 leading-tight">
            血液データから、<br />
            肝臓の状態を<span className="text-blue-600">"知る"</span>ための第一歩
          </h1>
          
          <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto leading-relaxed">
            国際指標を用いた、医療行為を伴わないセルフチェックサービス
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-8">
            <motion.button
              onClick={() => navigate('/check')}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="px-10 py-5 bg-gradient-to-r from-blue-600 to-green-600 text-white text-lg font-bold rounded-full hover:from-blue-700 hover:to-green-700 transition-all duration-300 shadow-xl whitespace-nowrap cursor-pointer"
            >
              無料でチェックする
            </motion.button>

            <motion.button
              onClick={() => navigate('/liver-function')}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="px-10 py-5 bg-white text-amber-600 text-lg font-bold rounded-full border-2 border-amber-600 hover:bg-amber-50 transition-all duration-300 shadow-lg whitespace-nowrap cursor-pointer"
            >
              肝臓の役割
            </motion.button>

            <motion.button
              onClick={() => navigate('/about-fib4')}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="px-10 py-5 bg-white text-blue-600 text-lg font-bold rounded-full border-2 border-blue-600 hover:bg-blue-50 transition-all duration-300 shadow-lg whitespace-nowrap cursor-pointer"
            >
              肝線維化のリスクを知りたい
            </motion.button>

            <motion.button
              onClick={() => navigate('/liver-progression')}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="px-10 py-5 bg-white text-green-600 text-lg font-bold rounded-full border-2 border-green-600 hover:bg-green-50 transition-all duration-300 shadow-lg whitespace-nowrap cursor-pointer"
            >
              脂肪肝から肝硬変への進行リスク
            </motion.button>
          </div>

          {/* 注意文 */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="max-w-2xl mx-auto p-5 bg-blue-50 border-l-4 border-blue-500 rounded-lg"
          >
            <div className="flex items-start gap-3">
              <div className="w-6 h-6 flex items-center justify-center flex-shrink-0 mt-0.5">
                <i className="ri-information-line text-blue-600 text-xl"></i>
              </div>
              <p className="text-sm text-gray-700 text-left leading-relaxed">
                <strong>※ 本サービスは医療行為・診断を行うものではありません。</strong><br />
                健康情報の提供を目的としています。
              </p>
            </div>
          </motion.div>
        </motion.div>

        {/* Feature Icons */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.5 }}
          className="grid md:grid-cols-3 gap-8 mt-20"
        >
          <div className="p-6 bg-white rounded-2xl shadow-md border border-gray-100">
            <div className="w-16 h-16 flex items-center justify-center bg-blue-100 rounded-full mx-auto mb-4">
              <i className="ri-shield-check-line text-blue-600 text-3xl"></i>
            </div>
            <h3 className="text-lg font-bold text-gray-900 mb-2">匿名で利用可能</h3>
            <p className="text-sm text-gray-600 leading-relaxed">
              氏名・住所は不要。安心してご利用いただけます。
            </p>
          </div>

          <div className="p-6 bg-white rounded-2xl shadow-md border border-gray-100">
            <div className="w-16 h-16 flex items-center justify-center bg-green-100 rounded-full mx-auto mb-4">
              <i className="ri-global-line text-green-600 text-3xl"></i>
            </div>
            <h3 className="text-lg font-bold text-gray-900 mb-2">国際指標を使用</h3>
            <p className="text-sm text-gray-600 leading-relaxed">
              世界的に認められた指標で状態を確認できます。
            </p>
          </div>

          <div className="p-6 bg-white rounded-2xl shadow-md border border-gray-100">
            <div className="w-16 h-16 flex items-center justify-center bg-purple-100 rounded-full mx-auto mb-4">
              <i className="ri-smartphone-line text-purple-600 text-3xl"></i>
            </div>
            <h3 className="text-lg font-bold text-gray-900 mb-2">スマホで簡単</h3>
            <p className="text-sm text-gray-600 leading-relaxed">
              いつでもどこでも、数分でチェック完了。
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default HeroSection;
